import { fromEvent, Observable, switchMap } from 'rxjs';
import { startWith } from 'rxjs/operators';

import {
    Component, ComponentFactoryResolver, ComponentRef, Directive, ElementRef, Inject, Input, OnInit,
    Optional, Self, TemplateRef, ViewContainerRef
} from '@angular/core';
import { AbstractControl, ControlContainer, NgControl, ValidationErrors } from '@angular/forms';
import {
    ControlErrorComponent, DefaultControlErrorComponent, FORM_ERRORS
} from '@ngneat/error-tailor';
import { ErrorsMap } from '@ngneat/error-tailor/lib/types';

@Component({
	template: `<span class="text-red-500 hidden top-7 absolute font-semibold text-xs">{{
		errorText
	}}</span>`,
})
export class IonicControlErrorComponent extends DefaultControlErrorComponent {}
@Directive({
	selector: '[inputError]',
})
export class InputErrorDirective implements OnInit {
	@Input() controlErrorsTpl: TemplateRef<any> | undefined;
	private ref: ComponentRef<ControlErrorComponent>;
	private anchor: ViewContainerRef;
	private control: AbstractControl;
	customErrors: ErrorsMap = {
		required: 'This field is required',
		email: 'Only valid emails',
		minlength: ({ requiredLength, actualLength }) =>
			`Expect ${requiredLength} letters but got ${actualLength} letters`,
		invalidAddress: error => `Address isn't valid`,
		pattern: 'This field does not match the required patttern',
	};

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private el: ElementRef,
		private vcr: ViewContainerRef,
		private resolver: ComponentFactoryResolver,
		@Optional() @Self() private ngControl: NgControl,
		@Optional() @Self() private controlContainer: ControlContainer,
		@Inject(FORM_ERRORS) private globalErrors
	) {}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - ngOnInit
	 **-------------------------------------------------------------------------------------
	 */
	ngOnInit(): void {
		this.control = (this.controlContainer || this.ngControl).control;
		this.anchor = this.resolveAnchor();
		const valueChanges$ = this.control.valueChanges;
		let changesOnBlur$: Observable<any>;
		let changesOnChange$: Observable<any>;
		const blur$ = fromEvent(this.el.nativeElement, 'focusout');
		changesOnBlur$ = blur$.pipe(switchMap(() => valueChanges$.pipe(startWith(true))));
		changesOnChange$ = valueChanges$;
		changesOnBlur$.subscribe(() => this.valueChanges());
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - valueChanges
	 **-------------------------------------------------------------------------------------
	 */
	private valueChanges() {
		const controlErrors = this.control.errors;
		if (controlErrors) {
			const [firstKey] = Object.keys(controlErrors);
			const getError = this.customErrors[firstKey] || this.globalErrors[firstKey];
			if (!getError) {
				return;
			}

			const text =
				typeof getError === 'function' ? getError(controlErrors[firstKey]) : getError;
			this.el.nativeElement.parentElement.classList.add('error-tailor-has-error');
			this.setError(text, controlErrors);
		} else if (this.ref) {
			this.el.nativeElement.parentElement.classList.remove('error-tailor-has-error');
			this.setError(null);
		}
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - setErrors
	 **-------------------------------------------------------------------------------------
	 */
	private setError(text: string, error?: ValidationErrors) {
		if (!this.ref) {
			const factory = this.resolver.resolveComponentFactory<ControlErrorComponent>(
				IonicControlErrorComponent
			);
			this.ref = this.anchor.createComponent<ControlErrorComponent>(factory);
		}
		const instance = this.ref.instance;
		instance.text = text;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - resolveAnchor
	 **-------------------------------------------------------------------------------------
	 */
	private resolveAnchor() {
		return this.vcr;
	}
}
